import { AuditService } from "@/services/auditService";
import { ActionTree } from "vuex";
import { SiteMonitorCamerasState } from "./types";
import { RootState } from "../types";
import api from "@/services/api.service";

export const actions: ActionTree<SiteMonitorCamerasState, RootState> = {
	activeMapItemsFromStorage() {
		return new Promise((resolve, reject) => {
			const activeMapItems = localStorage.getItem("activeMapItems") as string;

			if (activeMapItems) {
				const data = JSON.parse(activeMapItems);
				resolve(data);
			} else {
				reject();
			}
		});
	},

	async fetchAreaCameras({ rootState, commit }, groupID: number) {
		let pageNumber = 1;
		let groupDevices = [];

		// More than 100 pages = 50000 cameras = unnecessary amount for one area to show.
		while (pageNumber <= 100) {
			const newDevices = (
				await api.devicesByGroup(groupID, {
					pageSize: 500,
					page: pageNumber
				})
			).data;
			groupDevices = groupDevices.concat(newDevices);

			if (newDevices.length < 500) {
				// Less than total page size, so we've run out of cameras.
				break;
			}
			pageNumber++;
		}

		commit(
			"setAreaCameras",
			groupDevices
				.filter((device: any) => device.deviceTypeID === 1)
				.map(camera => {
					return {
						objectID: camera.deviceID,
						itemType: 1,
						title: camera.title,
						index: null
					};
				})
		);
	},

	/**
	 * Fetches the list of stored files for the current event, and commits them to the store.
	 * @param param0 The commit method.
	 * @param param1 An object containing the event id, audit endpoint, and audit session auth string.
	 */
	async fetchStoredFiles(
		{ commit },
		{ eventId, auditEndpoint, session }: { eventId: number; auditEndpoint: string; session: string }
	) {
		const auditService = new AuditService(auditEndpoint);
		const { data } = await auditService.retrieveStoredFileList(session, eventId);

		// Update the stored file list in the store, ensuring we map to camelCase.
		commit(
			"setDeviceControllerStoredFiles",
			data.map(storedFile => ({
				fileType: storedFile.FileType,
				fileLocation: storedFile.FileLocation,
				fileName: storedFile.FileName,
				eventRecordId: storedFile.EventRecordId
			}))
		);
	},

	async updateMediaMatrixContents({ commit }, { index, eventID, contents, isController }: any) {
		commit("updateMediaMatrixContents", { contents, index });
		commit("setMatrixContents", {
			index,
			contents
		});

		if (isController && eventID > 0) {
			const saveContents = {
				clip: null as any,
				camera: null as any,
				storedFile: null as any
			};

			if (contents.clip != null) {
				saveContents.clip = {
					fileIdentifier: contents.clip.clip ? contents.clip.clip.FileIdentifier : "",
					eventRecordID: contents.clip.eventRecordID
				};
			} else if (contents.camera != null) {
				saveContents.camera = { deviceId: contents.camera.objectID };
			} else if (contents.storedFile != null) {
				saveContents.storedFile = contents.storedFile;
			}

			await api.updateMatrixCell({
				eventID,
				cellIndex: index,
				contents: JSON.stringify(saveContents)
			});
		}
	},

	async fetchDevicesByGroup({ commit }, { groupID, pageSize, page, noCamRefOnly }) {
		const { data } = await api.devicesByGroup(groupID, { pageSize, page, noCamRefOnly });
		return data;
	},

	async fetchAllDevicesByGroup({ commit }, { groupID, noCamRefOnly }) {
		const devices = await api.allDevicesByGroup(groupID, { noCamRefOnly });
		return devices;
	},

	async loadDeviceById(context, { deviceId, params, regionUrl }) {
		const { data } = await api.loadDeviceById(deviceId, params, regionUrl);
		return data;
	},

	async saveMatrix(context, payload: any) {
		const { data } = await api.saveMatrix(payload);
		return data;
	},

	async searchDevicesNear({ rootGetters }, { search, deviceTypeID, groupID }) {
		const { data } = await api.deviceSearchNear({
			LatLong: rootGetters["siteMonitor/getMapCircleCenter"],
			search,
			deviceTypeID,
			groupID
		});

		if (rootGetters.getUseImperialMeasurements) {
			data.forEach(distance => {
				if (distance.distance) {
					distance.distance = metersToFeet(distance.distance);
					distance.imperial = true;
				}
			});
		}

		return data;
	},

	async searchForOutputs({ commit, dispatch }, { query, groupID }) {
		const data = await dispatch("searchDevicesNear", {
			search: query,
			deviceTypeID: 3,
			groupID
		});
		data.forEach(output => {
			output.controlRequest = false;
			output.controlReason = "";
			output.controlError = "";
			output.sendingCommand = false;
		});
		commit("setSearchedOutputs", data);
	},

	async searchForCameras({ commit, dispatch }, { query, groupID }) {
		const data = await dispatch("searchDevicesNear", {
			search: query,
			deviceTypeID: 1,
			groupID
		});
		commit("setSearchedCameras", data);
	},

	async getAllDeviceDetails({ rootGetters }, { deviceIDs, eventID, latLong }) {
		const details = await api.getMatrixDeviceDetails(deviceIDs, eventID, latLong);

		if (details !== null || details !== undefined) {
			for (const deviceDetails of details) {
				if (rootGetters.getUseImperialMeasurements && deviceDetails.distance) {
					deviceDetails.distance = metersToFeet(deviceDetails.distance);
					deviceDetails.imperial = true;
				}
			}
		}

		return details;
	},

	async setCameraScreenshot({ commit }, data: any) {
		api.saveCameraScreenshot(data);
	},

	async getDevicesByMapLayerItemType({ commit }, { groupID, deviceTypeID, filter }) {
		const data = await api.getDevicesByMapLayerItemType(groupID, deviceTypeID, filter);
		return data;
	}
};

// Pure helper fn
const metersToFeet = (meters: number) => {
	if (meters < 0) {
		return 0;
	} else {
		return Math.round(meters / 0.3048);
	}
};
